import * as React from 'react';
import {
  ProgramsState,
  LevelsState,
  Program,
  Level,
  Schedule,
  ProgramMemberDetail,
  ProgramsResponseData,
  TestHistory,
  ProgramMemberState
} from './Types';
import { 
  WEEKVIEW_DAYS, 
  EVENT_DAYS_ERROR_MESSAGE, 
  EVENT_TIME_ERROR_MESSAGE,
  UPDATED,
  CREATED,
  DELETED
} from './Constants';
import CommonFeatures from '../commons/CommonFeatures';
import Paper from 'material-ui/Paper';
import Chip from 'material-ui/Chip';
import TextField from 'material-ui/TextField';
import * as validator from 'validator';
import SelectField from 'material-ui/SelectField';
import MenuItem from 'material-ui/MenuItem';
import RaisedButton from 'material-ui/RaisedButton';
import TimePicker from 'material-ui/TimePicker';
import ReactTable from 'react-table';
import { Member, MembersState, MembersResponseData, TestResultCode } from '../members/members.types';
import IconButton from 'material-ui/IconButton';
import RemoveCircle from 'material-ui/svg-icons/content/remove-circle';
import { GithubPicker } from 'react-color';
import { Color } from '../calendar/calendar.event.types';
import FloatingActionButton from 'material-ui/FloatingActionButton';
import ContentAdd from 'material-ui/svg-icons/content/add';
import { testResults } from '../members/members.constants';
import Dialog from 'material-ui/Dialog';
import checkboxHOC from 'react-table/lib/hoc/selectTable';
import { overlayStyle, renderOverlayLoading } from '../util/Overlay';
import { formatTime } from '../util/time-util';
import Checkbox from 'material-ui/Checkbox';
import DatePicker from 'material-ui/DatePicker';
import { formatDate } from '../util/date-util';
import { isAdminAccount, isImpersonating } from '../admin/Util';
import { AuthState } from '../auth/auth.types';
import { cannotUpdate, cannotWrite, cannotDelete } from '../util/permission.util';
import { AdminState } from '../admin/admin.types';
import { PROGRAM } from '../account/Constants';
import { ScreenWidth } from '../app/app.const';
import { BusinessState } from 'src/business/business.types';
import { pseudoRandomBytes } from 'crypto';

type ProgramMember = {
  readonly programMemberDetail: ProgramMemberDetail
  readonly deletedTestHistories: TestHistory[]
  readonly newTestHistories: TestHistory[]
};

const DEFAULT_COLOR = '#0b9cd5';
const CheckboxTable = checkboxHOC(ReactTable);

const styles = {
  swatch: {
    padding: '5px',
    background: '#fff',
    borderRadius: '1px',
    boxShadow: '0 0 0 1px rgba(0,0,0,.1)',
    display: 'inline-block',
    cursor: 'pointer',
  }
};

type AddMemberTableData = {
  readonly id: number
  readonly memberId: string
  readonly name: string
  readonly phone: string
};

type TableDataType = {
  readonly memberProgramId: number
  readonly memberId: string
  readonly name: string
  readonly phone: string
  readonly deleteButton: JSX.Element
};

type Props = {
  readonly programMemberState: ProgramMemberState
  readonly programsState: ProgramsState
  readonly levelsState: LevelsState
  readonly membersState: MembersState
  readonly updateProgram: (
    id: number,
    name: string,
    colorValue: string
  ) => Promise<ProgramsResponseData>
  readonly createProgram: (
    name: string,
    levels: string[],
    schedules: Schedule[],
    colorValue: string
  ) => Promise<ProgramsResponseData>
  readonly deleteProgram: (id: number) => Promise<ProgramsResponseData>
  readonly toPrograms: () => void
  readonly getMember: (memberId: number) => Promise<MembersResponseData>
  readonly updateProgramLevels: (programId: number, levels: Level[]) => Promise<Response>
  readonly updateProgramSchedules: (programId: number, schedules: Schedule[]) => Promise<Response>
  readonly updateProgramMembers: (programMemberDetails: ProgramMemberDetail[]) => Promise<Response>
  readonly getProgramMembers: (id: number) => Promise<Response>
  readonly toAdminDashboard: () => void
  readonly authState: AuthState
  readonly businessState: BusinessState
  readonly adminState: AdminState
};

type FilterOption = 'Name' | 'Phone' | 'Level' | 'Required Attendances';

type States = {
  readonly programName: string
  readonly editedProgramName: string
  readonly programNameError: string
  readonly levels: Level[]
  readonly editedLevels: Level[]
  readonly selectedExistingLevel: Level
  readonly newLevelName: string
  readonly newLevelNameError: string
  readonly newRequiredAttendances: number
  readonly newRequiredAttendancesError: string
  readonly schedules: Schedule[]
  readonly editedSchedules: Schedule[]
  readonly lastEnteredDay: string
  readonly dayNotSelected: boolean
  readonly dayNotSelectedErrorMessage: string
  readonly lastEnteredStartTime: Date
  readonly lastEnteredEndTime: Date
  readonly startAndEndTimeErrorMessage: string
  readonly startAndEndTimeError: boolean
  readonly filteredData: ProgramMember[]
  readonly existingProgramMembers: ProgramMember[]
  readonly editedExistingProgramMembers: ProgramMember[]
  readonly membersToBeRemoved: ProgramMember[]
  readonly filterByName: string
  readonly filterByPhone: string
  readonly filterOption: FilterOption
  readonly filterValue: string
  readonly filterValueFloatingText: string
  readonly selectedTableRow: number
  readonly displayColorPicker: boolean
  readonly colorValue: string
  readonly editedColorValue: string
  readonly openAddMemberDialog: boolean
  readonly selectAll: boolean
  readonly selection: number[],
  readonly newAddedMembers: ProgramMember[]
  readonly filteredNewAddedMembers: ProgramMember[]
  readonly filterByNameAddNew: string
  readonly filterByPhoneAddNew: string
  readonly selectedScheduleIndex: number[]
  readonly openTestHistoryDialog: boolean
  readonly selectedTestHistoryLevel: string
  readonly selectedTestHistoryTestResult: TestResultCode
  readonly selectedTestHistoryDate: Date
  readonly submitErrorMessage: string
  readonly screenWidth: number
  readonly loadingProgramMembers: boolean
};

class ProgramDetailComponent extends React.Component<Props, States> {

  checkboxTable = null;
  
  constructor(props: Props) {
    super(props);

    this.state = {
      programName: props.programsState.selectedEvent ? props.programsState.selectedEvent.title : undefined,
      editedProgramName: props.programsState.selectedEvent ? props.programsState.selectedEvent.title : undefined,
      programNameError: undefined,
      levels: props.programsState.selectedEvent ? this.parseLevels(props.programsState.programs) : [],
      editedLevels: props.programsState.selectedEvent ? this.parseLevels(props.programsState.programs) : [],
      selectedExistingLevel: undefined,
      newLevelName: undefined,
      newLevelNameError: undefined,
      newRequiredAttendances: undefined,
      newRequiredAttendancesError: undefined,
      schedules: props.programsState.selectedEvent ? this.parseSchedules(props.programsState.programs) : [],
      editedSchedules: props.programsState.selectedEvent ? this.parseSchedules(props.programsState.programs) : [],
      lastEnteredDay: undefined,
      dayNotSelected: false,
      dayNotSelectedErrorMessage: undefined,
      lastEnteredStartTime: undefined,
      lastEnteredEndTime: undefined,
      startAndEndTimeErrorMessage: undefined,
      startAndEndTimeError: false,
      filteredData: [],
      membersToBeRemoved: [],
      filterByName: undefined,
      filterByPhone: undefined,
      filterOption: 'Name',
      filterValue: undefined,
      filterValueFloatingText: 'Filter By Name',
      selectedTableRow: undefined,
      displayColorPicker: false,
      colorValue: props.programsState.selectedEvent && props.programsState.selectedEvent.backgroundColor ?
        props.programsState.selectedEvent.backgroundColor :
        DEFAULT_COLOR,
      editedColorValue: props.programsState.selectedEvent && props.programsState.selectedEvent.backgroundColor ?
        props.programsState.selectedEvent.backgroundColor :
        DEFAULT_COLOR,
      openAddMemberDialog: false,
      selectAll: false,
      selection: [],
      newAddedMembers: [],
      filteredNewAddedMembers: [],
      filterByNameAddNew: undefined,
      filterByPhoneAddNew: undefined,
      selectedScheduleIndex: [],
      existingProgramMembers: [],
      editedExistingProgramMembers: [],
      openTestHistoryDialog: false,
      selectedTestHistoryTestResult: undefined,
      selectedTestHistoryLevel: undefined,
      selectedTestHistoryDate: undefined,
      submitErrorMessage: undefined,
      screenWidth: window.innerWidth,
      loadingProgramMembers: false
    };
  }

  componentWillMount() {
    this.updateWindowDimensions();
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.updateWindowDimensions);

    this.setState({
      programName: undefined,
      editedProgramName: undefined,
      programNameError: undefined,
      levels: [],
      editedLevels: [],
      selectedExistingLevel: undefined,
      newLevelName: undefined,
      newRequiredAttendances: undefined,
      filteredData: [],
      membersToBeRemoved: [],
      schedules: [],
      editedSchedules: [],
      lastEnteredDay: undefined,
      dayNotSelected: false,
      dayNotSelectedErrorMessage: undefined,
      lastEnteredStartTime: undefined,
      lastEnteredEndTime: undefined,
      startAndEndTimeErrorMessage: undefined,
      startAndEndTimeError: false,
      filterByName: undefined,
      filterByPhone: undefined,
      selectedTableRow: undefined,
      displayColorPicker: false,
      openAddMemberDialog: false,
      selectAll: false,
      selection: [],
      newAddedMembers: [],
      filterByNameAddNew: undefined,
      filterByPhoneAddNew: undefined,
      openTestHistoryDialog: false,
      submitErrorMessage: undefined,
      loadingProgramMembers: false
    });
  }

  componentDidMount() {
    window.addEventListener('resize', this.updateWindowDimensions);

    if (isAdminAccount(this.props.authState) && !isImpersonating(this.props.adminState)) {
      return this.props.toAdminDashboard();
    }

    this.setState(
      {loadingProgramMembers: true},
      () => {
        this.props.getProgramMembers(this.props.programsState.selectedEvent.id)
        .then(response => {
          switch (this.props.programMemberState.statusCode) {
            case 200:
              return this.setState(
                {
                  filteredData: this.props.programMemberState.programMembers
                    .map((p: ProgramMemberDetail) => {
                      return {
                        programMemberDetail: p,
                        newTestHistories: [],
                        deletedTestHistories: []
                      };
                    }),
                  existingProgramMembers: this.props.programMemberState.programMembers
                    .map((p: ProgramMemberDetail) => {
                      return {
                        programMemberDetail: p,
                        newTestHistories: [],
                        deletedTestHistories: []
                      };
                    }),
                  editedExistingProgramMembers: this.props.programMemberState.programMembers
                    .map((p: ProgramMemberDetail) => {
                      return {
                        programMemberDetail: p,
                        newTestHistories: [],
                        deletedTestHistories: []
                      };
                    }),
                  loadingProgramMembers: false
                }
              );
            default:
              this.setState({loadingProgramMembers: false});
              break;
          }
        });
      }
    );
  }

  render() {
    return (
      <CommonFeatures
        authState={this.props.authState}
        businessState={this.props.businessState}
      >
        <div className="row mt2 mb2">
          <div className="col-auto">
            <TextField
              floatingLabelText="Program Name"
              value={this.state.editedProgramName}
              onChange={this.handleProgramNameChange}
              errorText={this.state.programNameError}
            />
          </div>
          <div className="col-auto pt4">
            <div style={styles.swatch} onClick={this.handleClick}>
              <div style={this.createColorCss()} />
            </div>
            {
              this.state.displayColorPicker ?
                <div style={{position: 'absolute', zIndex: 2}}>
                  <div
                    style={{
                      position: 'fixed',
                      top: '0px',
                      right: '0px',
                      bottom: '0px',
                      left: '0px',
                    }}
                    onClick={this.handleDisplayColorPickerClose}
                  />
                  <GithubPicker color={this.state.editedColorValue} onChangeComplete={this.handleChange} />
                </div> :
                null
            }
          </div>
        </div>
        {this.renderLevelsAndSchedules()}
        {this.renderProgramMembers()}
        {this.renderAddMemberDialog()}
        {this.renderTestHistoryDialog()}
      </CommonFeatures>
    );
  }

  updateWindowDimensions = (): void => {
    this.setState({screenWidth: window.innerWidth});
  }

  private handleClick = (): void => this.setState({displayColorPicker: !this.state.displayColorPicker});

  private createColorCss = () => {
    return {
      width: '36px',
      height: '14px',
      borderRadius: '2px',
      background: this.state.editedColorValue
    };
  }

  private handleChange = (color: Color) => this.setState({editedColorValue: color.hex});

  private handleDisplayColorPickerClose = (): void => this.setState({displayColorPicker: false});

  private handleProgramNameChange = (event: React.FormEvent<HTMLInputElement>, newValue: string): void =>
    this.setState({
      editedProgramName: newValue,
      programNameError: !validator.isAlphanumeric(newValue.trim().replace(/[ ()-/']/g, '')) ?
        'Please enter valid program name' :
        undefined
    })

  private handleNewLevelNameChange = (event: React.FormEvent<HTMLInputElement>, newValue: string): void => {
    if (this.state.selectedExistingLevel) {
      this.setState({
        selectedExistingLevel: { 
          id: this.state.selectedExistingLevel.id,
          level: newValue, 
          requiredAttendance: this.state.selectedExistingLevel.requiredAttendance
        },
        newLevelNameError: newValue !== '' && !validator.isAlphanumeric(newValue.trim().replace(/[ ()-/']/g, '')) ?
          'Please enter valid level' :
          undefined
      });
    } else {
      this.setState({
        newLevelName: newValue,
        newLevelNameError: newValue !== '' && !validator.isAlphanumeric(newValue.trim().replace(/[ ()-/']/g, '')) ?
          'Please enter valid level' :
          undefined
      });
    }
  }

  private handleNewRequiredAttendancesChange = (event: React.FormEvent<HTMLInputElement>, newValue: string): void => {
    if (this.state.selectedExistingLevel) {
      this.setState({
        selectedExistingLevel: { 
          id: this.state.selectedExistingLevel.id,
          level: this.state.selectedExistingLevel.level, 
          requiredAttendance: validator.isNumeric(newValue.trim().replace(/[ ()-/']/g, '')) ?
          Number(newValue) : undefined
        },
        newRequiredAttendancesError: newValue !== '' && !validator.isNumeric(newValue.trim().replace(/[ ()-/']/g, '')) ?
          'Please enter valid required attendances' :
          undefined
      });
    } else {
      this.setState({
        newRequiredAttendances: validator.isNumeric(newValue.trim().replace(/[ ()-/']/g, '')) ?
          Number(newValue) : undefined,
        newRequiredAttendancesError: newValue !== '' && !validator.isNumeric(newValue.trim().replace(/[ ()-/']/g, '')) ?
          'Please enter valid required attendances' :
          undefined
      });
    }
  }

  private parseLevels = (programs: Program[]): Level[] => {
    const filtered: Program[] = programs.filter((p: Program) => p.id === this.props.programsState.selectedEvent.id);
    const program: Program = filtered.length === 1 ? filtered[0] : undefined;

    return program ? program.levels : [];
  }

  private parseSchedules = (programs: Program[]): Schedule[] => {
    const filtered: Program[] = programs.filter((p: Program) => p.id === this.props.programsState.selectedEvent.id);
    const program: Program = filtered.length === 1 ? filtered[0] : undefined;

    return program ? program.schedules : [];
  }

  private renderLevelsAndSchedules = (): JSX.Element => {
    return(
      <div className="row mt-2">
        <div className="col-6">
          {this.renderLevelList()}
        </div>
        <div className="col-6">
          {this.renderScheduleList()}
        </div>
      </div>
    );
  }

  private renderLevelList = (): JSX.Element => {
    return(
      <Paper zDepth={2}>
        <div className="row">
          <b className="col-auto ml2 mt2">Levels</b>
        </div>
        <div className="row">
          <div className="col-6">
            <div className="border ma2" style={{height: 40 * 7, overflowY: 'auto'}}>
              {this.renderLevelChips()}
            </div>
          </div>
          <div className="col-6">
            <div className="row">
              <div className="col-12">
                <TextField
                  floatingLabelText="Level"
                  value={this.state.selectedExistingLevel ? 
                    this.state.selectedExistingLevel.level : this.state.newLevelName ?
                    this.state.newLevelName : ''}
                  onChange={this.handleNewLevelNameChange}
                  errorText={this.state.newLevelNameError}
                  style={{width: '80%'}}
                  fullWidth={true}
                />
              </div>
            </div>
            <div className="row">
              <div className="col-12">
                <TextField
                  floatingLabelText="Required Attendances"
                  value={this.state.selectedExistingLevel ?
                     this.state.selectedExistingLevel.requiredAttendance : this.state.newRequiredAttendances ?
                     this.state.newRequiredAttendances : ''}
                  onChange={this.handleNewRequiredAttendancesChange}
                  errorText={this.state.newRequiredAttendancesError}
                  style={{width: '80%'}}
                  fullWidth={true}
                />
              </div>
            </div>
            <div className="row mt3 mb3">
              <div className="col-10">
                <RaisedButton
                  label={this.state.screenWidth < ScreenWidth.Medium ? 'ADD' : 'ADD LEVEL & SAVE'}
                  labelStyle={{fontSize: this.state.screenWidth > ScreenWidth.Large ? 13 : 10}}
                  primary={true} 
                  disabled={this.state.newLevelNameError !== undefined}
                  onClick={this.onAddLevelClick}
                  fullWidth={true}
                />
              </div>
            </div>
          </div>
        </div>
      </Paper>
    );
  }

  private onAddLevelClick = (): void => {
    const { editedLevels, newLevelName, selectedExistingLevel,
      newRequiredAttendances } = this.state;
    var newLevels = [];
    if (selectedExistingLevel) {
      editedLevels.forEach(function(e) {
        if (e.id && e.id === selectedExistingLevel.id) {
          newLevels.push(selectedExistingLevel);
        } else {
          newLevels.push(e);
        }
      });
    } else {
      newLevels = [...editedLevels, {level: newLevelName, requiredAttendance: newRequiredAttendances}];
    }
    
    this.setState(
      {
        editedLevels: newLevels,
        selectedExistingLevel : undefined,
        newLevelName : undefined,
        newRequiredAttendances : undefined
      },
      () => {
        if (this.props.programsState.eventAction === 'EDIT') {
          this.props.updateProgramLevels(
            this.props.programsState.selectedEvent.id,
            this.state.editedLevels
          ).then(response => { 
            switch (this.props.programsState.statusCode) {
              case 200:
                return this.setState(
                  {
                    editedLevels: this.props.programsState.programs
                      .find((p: Program) => p.id === this.props.programsState.selectedEvent.id)
                      .levels
                  }
                );
              default:
                return;
            }
          });
        }
      }
    );
  }

  private renderLevelChips = (): JSX.Element => {
    return(
      <div>
        {this.state.editedLevels.map((l: Level, i: number) =>
          <Chip
            key={i}
            style={{height: 40, width: '98%'}}
            labelStyle={{height: 40, width: '100%', fontSize: 14}}
            className="mt-1 mt-b ml-1"
            onRequestDelete={() => this.handleRequestLevelDelete(l)}
            onClick={() => this.setState({selectedExistingLevel: l})}
          >
            {l.level} / {l.requiredAttendance}
          </Chip>
        )}
      </div>
    );
  }

  private handleRequestLevelDelete = (l: Level): void =>
    this.setState(
      {editedLevels: this.state.editedLevels.filter((level: Level) => level.level !== l.level)},
      () => {
        if (this.props.programsState.eventAction === 'EDIT') {
          this.props.updateProgramLevels(
            this.props.programsState.selectedEvent.id,
            this.state.editedLevels
          ).then(response => { 
            switch (this.props.programsState.statusCode) {
              case 200:
                return this.setState(
                  {
                    editedLevels: this.props.programsState.programs
                      .find((p: Program) => p.id === this.props.programsState.selectedEvent.id)
                      .levels
                  }
                );
              default:
                return;
            }
          });
        }
      }
    )

  private renderScheduleList = (): JSX.Element => {
    return(
      <Paper zDepth={2}>
        <div className="row">
          <b className="col-auto ml2 mt2">Schedules</b>
        </div>
        <div className="row">
          <div className="col-6">
            <div className="border ma2" style={{height: 40 * 7, overflowY: 'auto'}}>
              {this.renderScheduleChips()}
            </div>
          </div>
          <div className="col-6">
            <div className="row">
              <div className="col-12">
                <SelectField
                  value={this.state.lastEnteredDay}
                  onChange={(event, index, value) => 
                    this.setState({lastEnteredDay: value}, this.validateSelectedDays)
                  }
                  floatingLabelText="Day"
                  errorText={this.state.dayNotSelectedErrorMessage}
                  style={{width: '80%'}}
                >
                  {WEEKVIEW_DAYS}
                </SelectField>
              </div>
            </div>
            <div className="row">
              <div className="col-12">
                <TimePicker
                  hintText="Start Time"
                  floatingLabelText="Start Time"
                  onChange={(event: any, date: Date) =>
                    this.setState({lastEnteredStartTime: date}, this.validateStartAndEndTimes)
                  }
                  disabled={this.state.lastEnteredDay === undefined}
                  underlineDisabledStyle={{display: 'none'}}
                  textFieldStyle={{width: '70%'}}
                  errorText={this.state.startAndEndTimeErrorMessage}
                  style={{width: '80%'}}
                />
              </div>
            </div>
            <div className="row">
              <div className="col-12">
                <TimePicker
                  hintText="End Time"
                  floatingLabelText="End Time"
                  onChange={(event: any, date: Date) => 
                    this.setState({lastEnteredEndTime: date}, this.validateStartAndEndTimes)
                  }
                  disabled={this.state.lastEnteredDay === undefined}
                  underlineDisabledStyle={{display: 'none'}}
                  textFieldStyle={{width: '70%'}}
                  errorText={this.state.startAndEndTimeErrorMessage}
                  style={{width: '80%'}}
                />
              </div>
            </div>
            <div className="row mt3 mb3">
              <div className="col-10">
                <RaisedButton
                  label={this.state.screenWidth < ScreenWidth.Medium ? 'ADD' : 'ADD SCHEDULE & SAVE'}
                  labelStyle={{fontSize: this.state.screenWidth > ScreenWidth.Large ? 13 : 10}}
                  primary={true} 
                  disabled={
                    this.state.lastEnteredEndTime === undefined ||
                    this.state.lastEnteredStartTime === undefined ||
                    this.state.dayNotSelected ||
                    !this.areTimesValid(this.state.lastEnteredStartTime, this.state.lastEnteredEndTime)
                  }
                  onClick={
                    () => this.onAddScheduleClick(
                      this.state.lastEnteredDay,
                      this.state.lastEnteredStartTime,
                      this.state.lastEnteredEndTime
                    )
                  }
                  fullWidth={true}
                />
              </div>
            </div>
          </div>
        </div>
      </Paper>
    );
  }

  private onAddScheduleClick = (day: string, startTime: Date, endTime: Date): void => {
    const newScheduleDoesntExist = this.state.editedSchedules
      .filter((s: Schedule) => s.day === day &&
        s.startHour === startTime.getHours() &&
        s.startMinute === startTime.getMinutes() &&
        s.endHour === endTime.getHours() &&
        s.endMinute === endTime.getMinutes()
      ).length === 0;

    if (newScheduleDoesntExist) {
      this.setState(
        {
          editedSchedules: [
            ...this.state.editedSchedules,
            {
              id: 0,
              day: day,
              startHour: startTime.getHours(),
              startMinute: startTime.getMinutes(),
              endHour: endTime.getHours(),
              endMinute: endTime.getMinutes() 
            }
          ]
        },
        () => {
          if (this.props.programsState.eventAction === 'EDIT') {
            this.props.updateProgramSchedules(
              this.props.programsState.selectedEvent.id,
              this.state.editedSchedules
            ).then(response => { 
              switch (this.props.programsState.statusCode) {
                case 200:
                  return this.setState(
                    {
                      editedSchedules: this.props.programsState.programs
                        .find((p: Program) => p.id === this.props.programsState.selectedEvent.id)
                        .schedules
                    }
                  );
                default:
                  return;
              }
            });
          }
        }
      );
    }
  }

  private toggleSelection = (key, shift, row) => {
    /*
      Implementation of how to manage the selection state is up to the developer.
      This implementation uses an array stored in the component state.
      Other implementations could use object keys, a Javascript Set, or Redux... etc.
    */
    // start off with the existing state
    let selection = [
      ...this.state.selection
    ];
    const keyIndex = selection.indexOf(key);
    // check to see if the key exists
    if (keyIndex >= 0) {
      // it does exist so we will remove it using destructing
      selection = [
        ...selection.slice(0, keyIndex),
        ...selection.slice(keyIndex + 1)
      ];
    } else {
      // it does not exist so add it
      selection.push(key);
    }
    // update the state
    this.setState({ selection });
  }

  private validateStartAndEndTimes = (): void => {
    const {lastEnteredStartTime, lastEnteredEndTime} = this.state;
   
    const isValid = this.areTimesValid(lastEnteredStartTime, lastEnteredEndTime);

    this.setState({
      startAndEndTimeError: !isValid,
      startAndEndTimeErrorMessage: !isValid ? EVENT_TIME_ERROR_MESSAGE : ''
    });
  }

  private areTimesValid = (lastEnteredStartTime: Date | undefined, lastEnteredEndTime: Date | undefined): boolean => {
    if (lastEnteredStartTime === undefined || lastEnteredEndTime === undefined) {
      return false;
    }

    return lastEnteredStartTime < lastEnteredEndTime;
  }

  private validateSelectedDays = (): void =>
    this.setState({
      dayNotSelected: !this.state.lastEnteredDay,
      dayNotSelectedErrorMessage: !this.state.lastEnteredDay ? EVENT_DAYS_ERROR_MESSAGE : ''
    })

  private renderScheduleChips = (): JSX.Element => {
    return(
      <div>
        {this.state.editedSchedules.map((s: Schedule, i: number) =>
          <Chip
            key={i}
            style={{height: 40, width: '98%'}}
            labelStyle={{height: 40, width: '100%', fontSize: 11}}
            className="mt-1 mt-b ml-1"
            onRequestDelete={() => this.handleRequestScheduleDelete(s)}
          >
            {s.day + ' ' +
              s.startHour + ':' + this.appendZeroToSingleZero(s.startMinute) + ' - ' + 
              s.endHour + ':' + this.appendZeroToSingleZero(s.endMinute)}
          </Chip>
        )}
      </div>
    );
  }

  private appendZeroToSingleZero = (n: number): string => n.toString().length === 1 ? '0' + n : n.toString();  

  private handleRequestScheduleDelete = (s: Schedule): void =>
    this.setState(
      {
        editedSchedules: this.state.editedSchedules.filter((sch: Schedule) =>
          sch.id ?
          sch.id !== s.id :
          !(sch.day === s.day &&
            sch.startHour === s.startHour && sch.startMinute === s.startMinute &&
            sch.endHour === s.endHour && sch.endMinute === s.endMinute)
        )
      },
      () => {
        if (this.props.programsState.eventAction === 'EDIT') {
          this.props.updateProgramSchedules(
            this.props.programsState.selectedEvent.id,
            this.state.editedSchedules
          ).then(response => { 
            switch (this.props.programsState.statusCode) {
              case 200:
                return this.setState(
                  {
                    editedSchedules: this.props.programsState.programs
                      .find((p: Program) => p.id === this.props.programsState.selectedEvent.id)
                      .schedules
                  }
                );
              default:
                return;
            }
          });
        }
      }
    )

  private renderProgramMembers = (): JSX.Element => {
    return(
      <div className="row mt3 mb3">
        <div className="col-12">
          <Paper zDepth={2}>
            <div className="row justify-content-between">
              {
                this.props.programsState.eventAction === 'CREATE' ?
                  <b className="col-auto ml2 mt2">You can add members after program creation</b> :
                  <b className="col-auto ml2 mt2">Members</b>
              }
              <div className="col-auto mt2 mr3">
                <RaisedButton
                  label="ADD MEMBER"
                  primary={true}
                  onClick={() => this.setState({openAddMemberDialog: true})}
                  disabled={this.props.programsState.eventAction === 'CREATE'}
                />
              </div>
            </div>
            <div className="row mb2 mt2 pl2 pr4">
              <div className="col-6">
                {
                  this.props.programsState.eventAction !== 'CREATE' ?
                    <div className="row">
                      <div className="col-6">
                        <SelectField
                          floatingLabelText="Filter Option"
                          value={this.state.filterOption}
                          onChange={this.handleFilterOptionChange}
                        >
                          <MenuItem value={'Name'} primaryText={'Name'} />
                          <MenuItem value={'Phone'} primaryText={'Phone'} />
                          <MenuItem value={'Level'} primaryText={'Level'} />
                          <MenuItem value={'Required Attendances'} primaryText={'Required Attendances'} />
                        </SelectField>
                      </div>
                      <div className="col-6">
                        <TextField
                          floatingLabelText={this.state.filterValueFloatingText}
                          value={this.state.filterValue}
                          onChange={this.handleFilterValueChange}
                          fullWidth={true}
                        />
                      </div>
                    </div> :
                    undefined
                }
                <div className="row">
                  <div className="col-12">
                    {
                      this.props.programsState.eventAction !== 'CREATE' ?
                        <div
                          className="col-12"
                          style={overlayStyle(!this.state.loadingProgramMembers)}
                        >
                          {renderOverlayLoading(!this.state.loadingProgramMembers, '25%')}
                          <ReactTable
                            data={
                              [...this.state.filteredData, ...this.state.filteredNewAddedMembers]
                              .map((ms: ProgramMember) => this.createTableData(ms.programMemberDetail))
                            }
                            columns={[
                              {
                                accessor: 'name',
                                Header: 'Name',
                                style: {textAlign: 'center'}
                              },
                              {                                                                                       
                                accessor: 'phone',
                                Header: 'Phone',
                                style: {textAlign: 'center'}
                              },
                              {
                                accessor: 'deleteButton',
                                Header: '',
                                style: {textAlign: 'center', width: '5%'}
                              }
                            ]}
                            defaultPageSize={9}
                            className="-striped -highlight"
                            keyField={'memberProgramId'}
                            showPageSizeOptions={false}
                            getTrProps={(state, rowInfo, column, instance) => {
                              return {
                                onClick: (e, handleOriginal) => rowInfo ?
                                  this.getMemberAndSetSelectedTableRow(rowInfo.index) :
                                  this.setState({selectedTableRow: undefined}),
                                style: {
                                  background: rowInfo && rowInfo.index === this.state.selectedTableRow ?
                                    '#00afec' :
                                    'white'
                                }
                              };
                            }}
                          />
                        </div> :
                        undefined
                    }
                  </div>
                </div>
              </div>
              {this.renderProgramMemberDetail()}
            </div>
            <div className="row justify-content-end">
              <div className="col-auto mb2 red">
                {this.state.submitErrorMessage}
              </div>
              {this.renderProgramDeleteButton()}
              <div className="col-auto mb2">
                <RaisedButton
                  label="CANCEL"
                  onClick={this.props.toPrograms}
                />
              </div>
              <div className="col-auto mr3 mb2">
                <RaisedButton
                  label={ this.props.programsState.eventAction === 'CREATE' ?
                    cannotWrite(PROGRAM, this.props.authState, this.props.adminState) ? 
                      'Not Enough Permission' : 
                      'CREATE' :
                    cannotUpdate(PROGRAM, this.props.authState, this.props.adminState) ? 
                      'Not Enough Permission' : 
                      'SAVE ALL CHANGES'
                  }
                  primary={true}
                  onClick={this.submitProgramDetail}
                  disabled={
                    this.props.programsState.eventAction === 'CREATE' ?
                      (
                        this.errorExistsProgramDetailCreation() || 
                        cannotWrite(PROGRAM, this.props.authState, this.props.adminState)
                      ) :
                      (
                        this.errorExistsProgramDetailEdit() || 
                        this.nothingChanged() ||
                        cannotUpdate(PROGRAM, this.props.authState, this.props.adminState)
                      )
                  }
                />
              </div>
            </div>
          </Paper>
        </div>
      </div>
    );
  }

  private renderProgramDeleteButton = (): JSX.Element => {
    if (this.props.programsState.eventAction === 'CREATE') {
      return(null);
    } else {
      return(
        <div className="col-auto mb2">
          <RaisedButton
            secondary={true}
            label={cannotDelete(PROGRAM, this.props.authState, this.props.adminState) ? 
              'Not Enough Permission' : 
              'DELETE'
            }
            onClick={() => 
              this.props.deleteProgram(this.props.programsState.selectedEvent.id)
              .then(response => {
                switch (this.props.programsState.statusCode) {
                  case 200:
                    return this.props.toPrograms();
                  default:
                    break;
                }
              })
            }
            disabled={cannotDelete(PROGRAM, this.props.authState, this.props.adminState)}
          />
        </div>
      );
    }
  }

  private errorExistsProgramDetailCreation = (): boolean => {
    return this.state.editedProgramName === undefined || this.state.programNameError !== undefined ||
      this.state.editedLevels.length === 0 || this.state.editedSchedules.length === 0;
  }

  private errorExistsProgramDetailEdit = (): boolean => {
    return this.state.editedProgramName === undefined || this.state.programNameError !== undefined ||
      this.state.editedLevels.length === 0 || this.state.editedSchedules.length === 0;
  }

  private nothingChanged = (): boolean => {
    return this.state.levels.some((l: Level) =>
      this.state.editedLevels.map((le: Level) => le.level).includes(l.level)) &&
      this.state.levels.length === this.state.editedLevels.length &&
      this.state.schedules
        .map((s: Schedule) => s.day + ' ' + formatTime(s.startHour, s.startMinute)
          + ' - ' + formatTime(s.endHour, s.endMinute))
        .some((s: string) =>
          this.state.editedSchedules
          .map((sc: Schedule) => sc.day + ' ' + formatTime(sc.startHour, sc.startMinute) + ' - ' +
            formatTime(sc.endHour, sc.endMinute))
          .includes(s)) &&
      this.state.schedules.length === this.state.editedSchedules.length &&
      this.state.programName === this.state.editedProgramName &&
      this.state.newAddedMembers.length === 0 &&
      this.state.colorValue === this.state.editedColorValue &&
      this.hasNothingChangedForExistingProgramMembers();
  }

  private hasNothingChangedForExistingProgramMembers = (): boolean => {
    const anythingChanged = this.state.existingProgramMembers
      .map((pr: ProgramMember) => {
        const e = this.state.editedExistingProgramMembers
          .find((p: ProgramMember) => pr.programMemberDetail.id === p.programMemberDetail.id);

        return (
          pr.programMemberDetail.level && e.programMemberDetail.level ?
            pr.programMemberDetail.level.level === e.programMemberDetail.level.level :
            pr.programMemberDetail.level === undefined && e.programMemberDetail.level === undefined
          ) &&
          pr.programMemberDetail.schedules.length === e.programMemberDetail.schedules.length &&
          (pr.programMemberDetail.testHistories && e.programMemberDetail.testHistories ?
            (
              pr.programMemberDetail.testHistories.length === e.programMemberDetail.testHistories.length &&
              pr.newTestHistories.length === e.newTestHistories.length &&
              pr.deletedTestHistories.length === e.deletedTestHistories.length
            ) :
            pr.programMemberDetail.testHistories === e.programMemberDetail.testHistories
          );
      });

    return this.state.membersToBeRemoved.length === 0 &&
      anythingChanged.includes(false) === false;
  }

  private submitProgramDetail = (): void => {
    if (this.props.programsState.eventAction === 'CREATE') {
      this.props.createProgram(
        this.state.editedProgramName,
        this.state.editedLevels.map((l: Level) => l.level),
        this.state.editedSchedules,
        this.state.editedColorValue
      ).then(response => {
        switch (this.props.programsState.statusCode) {
          case 200:
            return this.setState(
              {submitErrorMessage: undefined},
              this.props.toPrograms
            );
          case 400:
            return this.setState({submitErrorMessage: 'There is error in inputs.'});
          default:
            return;
        }
      });
    } else {
      if (!this.hasNothingChangedInProgramNameAndColor()) {
        this.props.updateProgram(
          this.props.programsState.selectedEvent.id,
          this.state.editedProgramName,
          this.state.editedColorValue
        ).then(response => {
          switch (this.props.programsState.statusCode) {
            case 200:
              return this.setState(
                {submitErrorMessage: undefined},
                this.callUpdateProgramMember
              );
            case 400:
              return this.setState({submitErrorMessage: 'There is an error in input.'});
            default:
              return;
          }
        });
      } else {
        return this.callUpdateProgramMember();
      }
    }
  }

  private callUpdateProgramMember = (): void => {
    this.props.updateProgramMembers(
      [
        ...this.createUpdatedProgramMemberToSubmit(),
        ...this.createDeletedProgramMemberToSubmit(),
        ...this.state.newAddedMembers
          .map((pm: ProgramMember) => {
            return {
              id: 0,
              programId: this.props.programsState.selectedEvent.id,
              levelId: pm.programMemberDetail.level.id,
              memberId: pm.programMemberDetail.member.id,
              scheduleIds: pm.programMemberDetail.schedules.map((s: Schedule) => s.id),
              testHistories: [
                ...pm.newTestHistories,
                ...pm.deletedTestHistories
              ],
              completedAttendances: 0,
              entityStatus: CREATED
            };
          })
      ]
    ).then(response => {
      switch (this.props.programsState.statusCode) {
        case 200:
          return this.props.toPrograms();
        default:
          return;
      }
    });
  }

  private createUpdatedProgramMemberToSubmit = (): ProgramMemberDetail[] => {
    return this.state.editedExistingProgramMembers
      .filter((pm: ProgramMember) => {
        const toBeDeleted = this.state.membersToBeRemoved.map((p: ProgramMember) => 
          p.programMemberDetail.id).includes(pm.programMemberDetail.id);
        
        const existing = this.state.existingProgramMembers
          .find((p: ProgramMember) => pm.programMemberDetail.id === p.programMemberDetail.id);

        if (existing) {
          return (
            !toBeDeleted &&
            !(
              (
                existing.programMemberDetail.level ? 
                  existing.programMemberDetail.level.id === pm.programMemberDetail.level.id &&
                  existing.programMemberDetail.level.level === pm.programMemberDetail.level.level :
                  pm.programMemberDetail.level === undefined
              ) &&
              existing.programMemberDetail.schedules.length === pm.programMemberDetail.schedules.length &&
              existing.programMemberDetail.schedules.filter((s: Schedule) =>
                !pm.programMemberDetail.schedules.find((sc: Schedule) => s.id === sc.id)).length === 0 &&
              pm.programMemberDetail.schedules.filter((s: Schedule) =>
                !existing.programMemberDetail.schedules.find((sc: Schedule) => s.id === sc.id)).length === 0 &&
              pm.newTestHistories.length === existing.newTestHistories.length &&
              pm.deletedTestHistories.length === existing.deletedTestHistories.length
            )
          );
        } else {
          return toBeDeleted;
        }
      })
      .map((pm: ProgramMember) => {
        return {
          id: pm.programMemberDetail.id,
          levelId: pm.programMemberDetail.level ? pm.programMemberDetail.level.id : undefined,
          programId: this.props.programsState.selectedEvent.id,
          memberId: pm.programMemberDetail.member.id,
          scheduleIds: pm.programMemberDetail.schedules.map((s: Schedule) => s.id),
          testHistories: [
            ...pm.newTestHistories,
            ...pm.deletedTestHistories
          ],
          completedAttendances: 0,
          entityStatus: UPDATED
        };
      });
  }

  private createDeletedProgramMemberToSubmit = (): ProgramMemberDetail[] => {
    return this.state.membersToBeRemoved
      .map((pm: ProgramMember) => {
        return {
          id: pm.programMemberDetail.id,
          levelId: pm.programMemberDetail.level ? pm.programMemberDetail.level.id : undefined,
          programId: this.props.programsState.selectedEvent.id,
          memberId: pm.programMemberDetail.member.id,
          scheduleIds: pm.programMemberDetail.schedules.map((s: Schedule) => s.id),
          testHistories: [
            ...pm.newTestHistories,
            ...pm.deletedTestHistories
          ],
          entityStatus: DELETED,
          completedAttendances: 0
        };
      });
  }

  private hasNothingChangedInProgramNameAndColor = (): boolean => {
    return this.state.programName === this.state.editedProgramName &&
      this.state.colorValue === this.state.editedColorValue;
  }

  private getMemberAndSetSelectedTableRow = (rowIndex: number): void => {
    this.setState(
      {selectedTableRow: rowIndex}
    );
  }

  private renderProgramMemberDetail = (): JSX.Element => {
    if (this.state.selectedTableRow !== undefined) {
      return(
        this.renderProgramMemberDetailSection()
      );
    } else {
      return(undefined);
    }
  }

  private renderProgramMemberDetailSection = (): JSX.Element => {
    const selectedMember = this.state.filteredData[this.state.selectedTableRow];
    const selectedFilteredNewMember = this.state.filteredNewAddedMembers[
      this.state.selectedTableRow - this.state.filteredData.length
    ];

    const found: Level = selectedMember ?
      this.state.editedExistingProgramMembers
        .find((m: ProgramMember) => m.programMemberDetail.member.id === selectedMember.programMemberDetail.member.id)
        .programMemberDetail
        .level :
      undefined;

    if (selectedMember) {
      return(
        <div className="col-6" style={{background: '#DEE1E1'}}>
          <div className="row mt2">
            <div className="col-6">
              <SelectField
                value={
                  found ? found.level : undefined
                }
                hintText="Select Level"
                onChange={(e: React.SyntheticEvent<{}>, index: number, menuItemValue: string) => 
                  this.onProgramMemberLevelChange(selectedMember, menuItemValue)}
                fullWidth={true}
              >
                {
                  this.state.editedLevels.map((l: Level, index: number) => {
                    return <MenuItem key={index} value={l.level} primaryText={l.level} />;
                  })
                }
              </SelectField>
              { (found && found.requiredAttendance > 0) ?
              <label>Required Attendances :  &nbsp;
              {selectedMember.programMemberDetail.completedAttendances} / {found.requiredAttendance}
              </label> :
              undefined
              }
            </div>
          </div>
          <div className="row">
            <div className="col-12">
              <hr style={{height: 1, borderColor: 'black'}}/>
            </div>
          </div>
          {
            this.renderSelectedMembersSchedules(selectedMember)
          }
          {this.renderTestHistorySection(selectedMember)}
        </div>
      );
    } else if (selectedFilteredNewMember) {
      return(
        <div className="col-6" style={{background: '#DEE1E1'}}>
          <div className="row mt2">
            <div className="col-6">
              <SelectField
                value={
                  selectedFilteredNewMember.programMemberDetail.level.level
                }
                hintText="Select Level"
                onChange={(e: React.SyntheticEvent<{}>, index: number, menuItemValue: string) => 
                  this.onProgramMemberLevelChange(selectedFilteredNewMember, menuItemValue, true)}
                fullWidth={true}
              >
                {
                  this.state.editedLevels.map((l: Level, index: number) => {
                    return <MenuItem key={index} value={l.level} primaryText={l.level} />;
                  })
                }
              </SelectField>
            </div>
          </div>
          <div className="row">
            <div className="col-12">
              <hr style={{height: 1, borderColor: 'black'}}/>
            </div>
          </div>
          {
            this.renderSelectedMembersSchedules(selectedFilteredNewMember, true)
          }
          {this.renderTestHistorySection(selectedFilteredNewMember, true)}
        </div>
      );
    } else {
      return(undefined);
    }
  }

  private onProgramMemberLevelChange = (
    ms: ProgramMember,
    menuItemValue: string,
    isNew?: boolean
  ): void => {
    const foundPM = isNew ? 
      this.state.filteredNewAddedMembers
        .find((m: ProgramMember) => m.programMemberDetail.member.id === ms.programMemberDetail.member.id) :
      this.state.filteredData
        .find((m: ProgramMember) => m.programMemberDetail.member.id === ms.programMemberDetail.member.id);
    const i = isNew ? this.state.filteredNewAddedMembers.indexOf(foundPM) : this.state.filteredData.indexOf(foundPM);

    const existingPM = this.state.editedExistingProgramMembers
      .find((m: ProgramMember) => m.programMemberDetail.member.id === ms.programMemberDetail.member.id);
    const existingIndex = this.state.editedExistingProgramMembers.indexOf(existingPM);

    const originalNewAdded = this.state.newAddedMembers.find((m: ProgramMember) =>
      m.programMemberDetail.member.id === ms.programMemberDetail.member.id);
    const originalIndex = this.state.newAddedMembers.indexOf(originalNewAdded);
    const foundLevel = this.state.editedLevels.find((l: Level) => l.level === menuItemValue);
    if (isNew) {
      this.setState({
        newAddedMembers: [
          ...this.state.newAddedMembers.slice(0, originalIndex),
          {
            ...originalNewAdded,
            programMemberDetail: {
              ...originalNewAdded.programMemberDetail,
              level: {
                id: foundLevel.id, 
                level: menuItemValue,
                requiredAttendance: foundLevel.requiredAttendance
              } 
            }
          },
          ...this.state.newAddedMembers.slice(originalIndex + 1)
        ],
        filteredNewAddedMembers: [
          ...this.state.filteredNewAddedMembers.slice(0, i),
          {
            ...foundPM,
            programMemberDetail: {
              ...foundPM.programMemberDetail,
              level: {
                id: foundLevel.id, 
                level: menuItemValue,
                requiredAttendance: foundLevel.requiredAttendance 
              } 
            }
          },
          ...this.state.filteredNewAddedMembers.slice(i + 1)
        ]
      });
    } else {
      this.setState({
        editedExistingProgramMembers: [
          ...this.state.editedExistingProgramMembers.slice(0, existingIndex),
          {
            ...existingPM,
            programMemberDetail: {
              ...existingPM.programMemberDetail,
              level: {
                id: foundLevel.id, 
                level: menuItemValue,
                requiredAttendance: foundLevel.requiredAttendance
              } 
            }
          },
          ...this.state.editedExistingProgramMembers.slice(existingIndex + 1)
        ],
        filteredData: [
          ...this.state.filteredData.slice(0, i),
          {
            ...foundPM,
            programMemberDetail: {
              ...foundPM.programMemberDetail,
              level: {
                id: foundLevel.id, 
                level: menuItemValue,
                requiredAttendance: foundLevel.requiredAttendance
              } 
            }
          },
          ...this.state.filteredData.slice(i + 1)
        ]
      });
    }
  }

  private renderSelectedMembersSchedules = (filtered: ProgramMember, isNew?: boolean): JSX.Element => {
    return( 
      <div className="row">
        {
          this.state.editedSchedules.map((schedule: Schedule, index: number) => {
            const menuText = schedule.day + ' ' + formatTime(schedule.startHour, schedule.startMinute)
              + ' - ' + formatTime(schedule.endHour, schedule.endMinute);
            const scheduleId = (schedule.id) ? schedule.id : 0;
            return <div className="col-12 pt-3" key={index}>
                    <div className="input-group mb-2 mr-sm-2 mb-sm-0">
                      <Checkbox
                        label={menuText}
                        checked={
                          filtered.programMemberDetail.schedules
                          .map((s: Schedule) => s.day + ' ' + formatTime(s.startHour, s.startMinute) + ' - ' + 
                            formatTime(s.endHour, s.endMinute))
                          .includes( 
                            this.state.editedSchedules[index].day + ' ' +
                            formatTime(
                              this.state.editedSchedules[index].startHour, 
                              this.state.editedSchedules[index].startMinute) +
                            ' - ' + 
                            formatTime(
                              this.state.editedSchedules[index].endHour, 
                              this.state.editedSchedules[index].endMinute)
                          )
                        }
                        value={scheduleId}
                        onCheck={
                          () => {
                            const foundPM = isNew ?
                              this.state.filteredNewAddedMembers
                                .find((m: ProgramMember) =>
                                m.programMemberDetail.member.id === filtered.programMemberDetail.member.id) :
                              this.state.filteredData
                                .find((m: ProgramMember) => 
                                m.programMemberDetail.member.id === filtered.programMemberDetail.member.id);
                            const i = isNew ?
                              this.state.filteredNewAddedMembers.indexOf(foundPM) :
                              this.state.filteredData.indexOf(foundPM);

                            const originalNewAdded = this.state.newAddedMembers.find((m: ProgramMember) =>
                              m.programMemberDetail.member.id === filtered.programMemberDetail.member.id);
                            const originalIndex = this.state.newAddedMembers.indexOf(originalNewAdded);

                            if (isNew) {
                              this.setState(
                                {
                                  newAddedMembers: [
                                    ...this.state.newAddedMembers.slice(0, originalIndex),
                                    {
                                      ...originalNewAdded,
                                      programMemberDetail: {
                                        ...originalNewAdded.programMemberDetail,
                                        schedules: originalNewAdded.programMemberDetail.schedules
                                          .map((s: Schedule) => s.id)
                                          .includes(schedule.id) ?
                                          [
                                            ...originalNewAdded.programMemberDetail.schedules.slice(
                                              0, originalNewAdded.programMemberDetail.schedules
                                                .map((s: Schedule) => s.id).indexOf(schedule.id)),
                                            ...originalNewAdded.programMemberDetail.schedules.slice(
                                              originalNewAdded.programMemberDetail.schedules
                                                .map((s: Schedule) => s.id).indexOf(schedule.id) + 1)
                                          ] :
                                          [...originalNewAdded.programMemberDetail.schedules, schedule]
                                      }
                                    },
                                    ...this.state.newAddedMembers.slice(originalIndex + 1)
                                  ],
                                  filteredNewAddedMembers: [
                                    ...this.state.filteredNewAddedMembers.slice(0, i),
                                    {
                                      ...foundPM,
                                      programMemberDetail: {
                                        ...foundPM.programMemberDetail,
                                        schedules: foundPM.programMemberDetail.schedules
                                          .map((s: Schedule) => s.id)
                                          .includes(schedule.id) ?
                                          [
                                            ...foundPM.programMemberDetail.schedules.slice(
                                              0, foundPM.programMemberDetail.schedules
                                                .map((s: Schedule) => s.id).indexOf(schedule.id)),
                                            ...foundPM.programMemberDetail.schedules.slice(
                                              foundPM.programMemberDetail.schedules
                                                .map((s: Schedule) => s.id).indexOf(schedule.id) + 1)
                                          ] :
                                          [ ...foundPM.programMemberDetail.schedules, schedule]
                                      }
                                    },
                                    ...this.state.filteredNewAddedMembers.slice(i + 1)
                                  ]
                                }
                              );
                            } else {
                              const existingPM = this.state.editedExistingProgramMembers.find((m: ProgramMember) => 
                                m.programMemberDetail.member.id === filtered.programMemberDetail.member.id);
                              const existingIndex = this.state.editedExistingProgramMembers.indexOf(existingPM);

                              this.setState(
                                {
                                  editedExistingProgramMembers: [
                                    ...this.state.editedExistingProgramMembers.slice(0, existingIndex),
                                    {
                                      ...existingPM,
                                      programMemberDetail: {
                                        ...existingPM.programMemberDetail,
                                        schedules: existingPM.programMemberDetail.schedules
                                          .map((s: Schedule) => s.id)
                                          .includes(schedule.id) ?
                                          [
                                            ...existingPM.programMemberDetail.schedules.slice(
                                              0, existingPM.programMemberDetail.schedules
                                                .map((s: Schedule) => s.id).indexOf(schedule.id)),
                                            ...existingPM.programMemberDetail.schedules.slice(
                                              existingPM.programMemberDetail.schedules
                                                .map((s: Schedule) => s.id).indexOf(schedule.id) + 1)
                                          ] :
                                          [ ...existingPM.programMemberDetail.schedules, schedule]
                                      }
                                    },
                                    ...this.state.editedExistingProgramMembers.slice(existingIndex + 1)
                                  ],
                                  filteredData: [
                                    ...this.state.filteredData.slice(0, i),
                                    {
                                      ...foundPM,
                                      programMemberDetail: {
                                        ...foundPM.programMemberDetail,
                                        schedules: foundPM.programMemberDetail.schedules
                                          .map((s: Schedule) => s.id)
                                          .includes(schedule.id) ?
                                          [
                                            ...foundPM.programMemberDetail.schedules.slice(
                                              0, foundPM.programMemberDetail.schedules
                                                .map((s: Schedule) => s.id).indexOf(schedule.id)),
                                            ...foundPM.programMemberDetail.schedules.slice(
                                              foundPM.programMemberDetail.schedules
                                                .map((s: Schedule) => s.id).indexOf(schedule.id) + 1)
                                          ] :
                                          [ ...foundPM.programMemberDetail.schedules, schedule]
                                      }
                                    },
                                    ...this.state.filteredData.slice(i + 1)
                                  ]
                                }
                              );
                            }
                        }
                      }
                      />
                    </div>
                  </div>;
          })
        }
      </div>
    );
  }

  private renderTestHistorySection = (pm: ProgramMember, isNew?: boolean): JSX.Element => {
    return(
      <div>
        <div className="row mt2">
          <b className="col-auto">
            Test History
          </b>
          <div className="col-auto" style={{padding: 0, margin: 0}}>
            <FloatingActionButton
              mini={true}
              iconStyle={{height: 25, width: 25}}
              onClick={() => this.setState({openTestHistoryDialog: true})}
            >
              <ContentAdd />
            </FloatingActionButton>
          </div>
        </div>
        <div className="row mt2 mb2">
          <div className="col-12 text-center">
            {
              <ReactTable
                data={
                  this.createTestHistoryDetailData(pm, isNew)
                }
                columns={[
                  {
                    accessor: 'date',
                    Header: 'Date',
                    style: {textAlign: 'center'}
                  },
                  {
                    accessor: 'level',
                    Header: 'Level',
                    style: {textAlign: 'center'}
                  },
                  {                                                                                       
                    accessor: 'result',
                    Header: 'Result',
                    style: {textAlign: 'center'}
                  },
                  {
                    accessor: 'deleteButton',
                    Header: '',
                    style: {textAlign: 'center', width: '5%'}
                  }
                ]}
                defaultPageSize={3}
                className="-striped -highlight"
                keyField={'testHistoryId'}
                showPageSizeOptions={false}
                getTrProps={(state, rowInfo, column, instance) => {
                  return {
                    style: {
                      background: 'white'
                    }
                  };
                }}
              />
            }
          </div>
        </div>
      </div>
    );
  }

  private createTestHistoryDetailData = (pm: ProgramMember, isNew?: boolean): {
    readonly date: string,
    readonly result: string,
    readonly deleteButton: JSX.Element
  }[] => {
    return [
      ...pm.programMemberDetail.testHistories
        .filter((t: TestHistory) => 
          !pm.deletedTestHistories.map((te: TestHistory) => te.testDate).includes(t.testDate) ||
          !pm.deletedTestHistories.map((te: TestHistory) => te.level).includes(t.level) ||
          !pm.deletedTestHistories.map((te: TestHistory) => te.levelName).includes(t.levelName) ||
          !pm.deletedTestHistories.map((te: TestHistory) => te.testResult).includes(t.testResult) ||
          !pm.deletedTestHistories.map((te: TestHistory) => te.programName).includes(t.programName)
        )
        .map((t: TestHistory) => {
          return {
            date: formatDate(t.testDate),
            level: t.levelName ? t.levelName : t.level,
            result: t.testResult,
            deleteButton: this.renderDeleteTestHistoryDetailButton(pm, t, isNew, false)
          };
        }),
      ...pm.newTestHistories
        .map((t: TestHistory) => {
          return {
            date: formatDate(t.testDate),
            level: t.levelName ? t.levelName : t.level,
            result: t.testResult,
            deleteButton: this.renderDeleteTestHistoryDetailButton(pm, t, isNew, true)
          };
        })
    ];
  }

  private handleFilterByNameChange = (event: React.FormEvent<HTMLInputElement>, newValue: string): void =>
    this.setState({filterByName: newValue}, this.applyFilters)

  private handleFilterByPhoneChange = (event: React.FormEvent<HTMLInputElement>, newValue: string): void =>
    this.setState({filterByPhone: newValue}, this.applyFilters)

  private handleFilterOptionChange = 
  (event: React.FormEvent<HTMLInputElement>, index: number, newValue: string): void => {
    var text = '';
    var newFilterOption;
    switch (newValue) {
      case 'Name':
        text = 'Filter By Name';
        newFilterOption = 'Name';
        break;
      case 'Phone':
        text = 'Filter By Phone';
        newFilterOption = 'Phone';
        break;
      case 'Level':
        text = 'Filter By Level';
        newFilterOption = 'Level';
        break;
      case 'Required Attendances':
        text = 'Filter By Required Attendances';
        newFilterOption = 'Required Attendances';
        break;
      default:
        break;
    }
    this.setState({filterValueFloatingText: text, filterOption: newFilterOption});
  }

  private handleFilterValueChange = (event: React.FormEvent<HTMLInputElement>, newValue: string): void =>
    this.setState({filterValue: newValue}, this.applyFilters)

  private handleFilterByNameChangeAddNew = (event: React.FormEvent<HTMLInputElement>, newValue: string): void =>
    this.setState({filterByNameAddNew: newValue})

  private handleFilterByPhoneChangeAddNew = (event: React.FormEvent<HTMLInputElement>, newValue: string): void =>
    this.setState({filterByPhoneAddNew: newValue})

  private createTableData = (pmd: ProgramMemberDetail): TableDataType => {
    return {
      memberProgramId: pmd.id,
      memberId: pmd.member.memberId,
      name: pmd.member.firstName + ' ' + pmd.member.lastName,
      phone: pmd.member.mobile1,
      deleteButton: this.renderDeleteButton(pmd.member.memberId)
    };
  }

  private renderDeleteButton = (memberId: string): JSX.Element => {
    return(
      <IconButton
        disableTouchRipple={true}
        iconStyle={{
          width: 18,
          height: 18
        }}
        style={{
          width: 20,
          height: 20
        }}
        onClick={() => this.prepareToRemoveMember(memberId)}
      >
        <RemoveCircle />
      </IconButton>
    );
  }

  private renderDeleteTestHistoryDetailButton = (
    pm: ProgramMember, 
    t: TestHistory,
    isNewMember?: boolean,
    isNew?: boolean
  ): JSX.Element => {
    return(
      <IconButton
        disableTouchRipple={true}
        iconStyle={{
          width: 18,
          height: 18
        }}
        style={{
          width: 20,
          height: 20
        }}
        onClick={() => this.prepareToRemoveTestHistoryDetail(pm, t, isNewMember, isNew)}
      >
        <RemoveCircle />
      </IconButton>
    );
  }

  private prepareToRemoveTestHistoryDetail = (
    pm: ProgramMember, 
    t: TestHistory, 
    isNewMember?: boolean, 
    isNew?: boolean
  ): void => {
    const foundPM: ProgramMember = isNewMember ? 
      this.state.filteredNewAddedMembers
        .find((m: ProgramMember) => m.programMemberDetail.member.id === pm.programMemberDetail.member.id) :
      this.state.filteredData
        .find((m: ProgramMember) => m.programMemberDetail.member.id === pm.programMemberDetail.member.id);
    const i: number = isNewMember ? 
      this.state.filteredNewAddedMembers.indexOf(foundPM) : 
      this.state.filteredData.indexOf(foundPM);

    const existingPM: ProgramMember = this.state.editedExistingProgramMembers
      .find((m: ProgramMember) => m.programMemberDetail.member.id === pm.programMemberDetail.member.id);
    const existingIndex: number = this.state.editedExistingProgramMembers.indexOf(existingPM);

    const originalNewAdded: ProgramMember = this.state.newAddedMembers
      .find((m: ProgramMember) => m.programMemberDetail.member.id === pm.programMemberDetail.member.id);
    const originalIndex: number = this.state.newAddedMembers.indexOf(originalNewAdded);

    if (!isNewMember && isNew) {
      this.setState(
        {
          editedExistingProgramMembers: [
            ...this.state.editedExistingProgramMembers.slice(0, existingIndex),
            {
              ...existingPM,
              newTestHistories: existingPM.newTestHistories.filter((te: TestHistory, index: number) =>
                (te.testDate !== t.testDate ||
                te.level !== t.level ||
                te.levelName !== t.levelName || 
                te.testResult !== t.testResult ||
                te.programName !== t.programName)
              )
            },
            ...this.state.editedExistingProgramMembers.slice(existingIndex + 1)
          ],
          filteredData: [
            ...this.state.filteredData.slice(0, i),
            {
              ...foundPM,
              newTestHistories: foundPM.newTestHistories.filter((te: TestHistory, index: number) =>
                (te.testDate !== t.testDate || 
                te.level !== t.level ||  
                te.levelName !== t.levelName || 
                te.testResult !== t.testResult ||
                te.programName !== t.programName)
              )
            },
            ...this.state.filteredData.slice(i + 1)
          ]
        }
      );
    } else if (!isNewMember && !isNew) {
      this.setState(
        {
          editedExistingProgramMembers: [
            ...this.state.editedExistingProgramMembers.slice(0, existingIndex),
            {
              ...existingPM,
              deletedTestHistories: 
              [
                ...existingPM.deletedTestHistories,
                {
                  id: t.id,
                  memberId: t.memberId,
                  programId: t.programId,
                  programName: t.programName,
                  levelId: t.levelId,
                  level: t.level,
                  levelName: t.levelName,
                  testResult: t.testResult,
                  testDate: t.testDate,
                  entityStatus: DELETED
                }
              ]
            },
            ...this.state.editedExistingProgramMembers.slice(existingIndex + 1)
          ],
          filteredData: [
            ...this.state.filteredData.slice(0, i),
            {
              ...foundPM,
              deletedTestHistories: 
              [
                ...foundPM.deletedTestHistories,
                {
                  id: t.id,
                  memberId: t.memberId,
                  programId: t.programId,
                  programName: t.programName,
                  levelId: t.levelId,
                  level: t.level,
                  levelName: t.levelName,
                  testResult: t.testResult,
                  testDate: t.testDate,
                  entityStatus: DELETED
                }
              ]
            },
            ...this.state.filteredData.slice(i + 1)
          ]
        }
      );
    } else if (isNewMember && isNew) {
      this.setState(
        {
          newAddedMembers: [
            ...this.state.newAddedMembers.slice(0, originalIndex),
            {
              ...originalNewAdded,
              newTestHistories: originalNewAdded.newTestHistories.filter((te: TestHistory) =>
                (te.testDate !== t.testDate ||
                te.level !== t.level ||
                te.levelName !== t.levelName || 
                te.testResult !== t.testResult ||
                te.programName !== t.programName))
            },
            ...this.state.newAddedMembers.slice(originalIndex + 1)
          ],
          filteredNewAddedMembers: [
            ...this.state.filteredNewAddedMembers.slice(0, i),
            {
              ...foundPM,
              newTestHistories: foundPM.newTestHistories.filter((te: TestHistory) =>
                (te.testDate !== t.testDate ||
                te.level !== t.level ||
                te.levelName !== t.levelName || 
                te.testResult !== t.testResult ||
                te.programName !== t.programName))
            },
            ...this.state.filteredNewAddedMembers.slice(i + 1)
          ]
        }
      );
    }
  }

  private prepareToRemoveMember = (memberId: string): void => {
    if (this.state.filteredData.find((ms: ProgramMember) => 
      ms.programMemberDetail.member.memberId === memberId
    )) {
      this.setState(
        {
          membersToBeRemoved: [
            ...this.state.membersToBeRemoved,
            this.state.filteredData.find((ms: ProgramMember) => 
            ms.programMemberDetail.member.memberId === memberId)
          ],
          selectedTableRow: undefined
        },
        this.applyFilters
      );
    } else {
      this.setState(
        {
          newAddedMembers: [...this.state.newAddedMembers
            .filter((ms: ProgramMember) => ms.programMemberDetail.member.memberId !== memberId)],
          selectedTableRow: undefined
        },
        this.applyFilters
      );
    }
  }

  private applyFilters = (): void => {
    var filteredExisting = [];
    var filteredNew = [];
    switch (this.state.filterOption) {
      case 'Name':
        filteredExisting = this.props.programMemberState.programMembers
        .filter((pmd: ProgramMemberDetail) =>
          !this.state.membersToBeRemoved.map((mem: ProgramMember) => mem.programMemberDetail.member.memberId)
            .includes(pmd.member.memberId) &&
          (this.state.filterValue && this.state.filterValue !== '' ?  
          (pmd.member.firstName + ' ' + pmd.member.lastName).toLowerCase()
              .includes(this.state.filterValue.toLowerCase()) : true))
        .map((pmd: ProgramMemberDetail) => {
          if (
            this.state.editedExistingProgramMembers
            .map((pr: ProgramMember) => pr.programMemberDetail.member.id)
            .includes(pmd.member.id)
          ) {
            return this.state.editedExistingProgramMembers
              .find((pm: ProgramMember) => pm.programMemberDetail.member.id === pmd.member.id);
          } else {
            return {
              programMemberDetail: pmd,
              deletedTestHistories: [],
              newTestHistories: []
            };
          }
        });
        filteredNew = this.state.newAddedMembers
        .filter((ms: ProgramMember) =>
          (this.state.filterValue && this.state.filterValue !== '' ?
          (ms.programMemberDetail.member.firstName + ' ' + ms.programMemberDetail.member.lastName).toLowerCase()
            .includes(this.state.filterByName.toLowerCase()) : true));
        break;
      case 'Phone':
        filteredExisting = this.props.programMemberState.programMembers
        .filter((pmd: ProgramMemberDetail) =>
          !this.state.membersToBeRemoved.map((mem: ProgramMember) => mem.programMemberDetail.member.memberId)
            .includes(pmd.member.memberId) &&
          ((pmd.member.mobile1).toLowerCase()
              .includes(this.state.filterValue.toLowerCase())))
        .map((pmd: ProgramMemberDetail) => {
          if (
            this.state.editedExistingProgramMembers
            .map((pr: ProgramMember) => pr.programMemberDetail.member.id)
            .includes(pmd.member.id)
          ) {
            return this.state.editedExistingProgramMembers
              .find((pm: ProgramMember) => pm.programMemberDetail.member.id === pmd.member.id);
          } else {
            return {
              programMemberDetail: pmd,
              deletedTestHistories: [],
              newTestHistories: []
            };
          }
        });
        filteredNew = this.state.newAddedMembers
        .filter((ms: ProgramMember) =>
          ((ms.programMemberDetail.member.mobile1).toLowerCase()
            .includes(this.state.filterValue.toLowerCase())));
        break;
      case 'Level':
        filteredExisting = this.props.programMemberState.programMembers
        .filter((pmd: ProgramMemberDetail) =>
          !this.state.membersToBeRemoved.map((mem: ProgramMember) => mem.programMemberDetail.member.memberId)
            .includes(pmd.member.memberId) &&
          ((this.state.filterValue && this.state.filterValue !== '' ? (pmd.level ? pmd.level.level.toLowerCase()
              .includes(this.state.filterValue.toLowerCase()) : false) : true)))
        .map((pmd: ProgramMemberDetail) => {
          if (
            this.state.editedExistingProgramMembers
            .map((pr: ProgramMember) => pr.programMemberDetail.member.id)
            .includes(pmd.member.id)
          ) {
            return this.state.editedExistingProgramMembers
              .find((pm: ProgramMember) => pm.programMemberDetail.member.id === pmd.member.id);
          } else {
            return {
              programMemberDetail: pmd,
              deletedTestHistories: [],
              newTestHistories: []
            };
          }
        });
        filteredNew = this.state.newAddedMembers
        .filter((ms: ProgramMember) =>
          (this.state.filterValue && this.state.filterValue !== '' ?
          (ms.programMemberDetail.level ? ms.programMemberDetail.level.level.toLowerCase()
            .includes(this.state.filterValue.toLowerCase()) : false) : true)); 
        break;
      case 'Required Attendances':
        filteredExisting = this.props.programMemberState.programMembers
        .filter((pmd: ProgramMemberDetail) =>
          !this.state.membersToBeRemoved.map((mem: ProgramMember) => mem.programMemberDetail.member.memberId)
            .includes(pmd.member.memberId) &&
          (this.state.filterValue && this.state.filterValue !== '' ?
          (pmd.level && pmd.level.requiredAttendance > 0 ? 
            pmd.level.requiredAttendance - pmd.completedAttendances === Number(this.state.filterValue) : false) 
            : true))
        .map((pmd: ProgramMemberDetail) => {
          if (
            this.state.editedExistingProgramMembers
            .map((pr: ProgramMember) => pr.programMemberDetail.member.id)
            .includes(pmd.member.id)
          ) {
            return this.state.editedExistingProgramMembers
              .find((pm: ProgramMember) => pm.programMemberDetail.member.id === pmd.member.id);
          } else {
            return {
              programMemberDetail: pmd,
              deletedTestHistories: [],
              newTestHistories: []
            };
          }
        });
        filteredNew = this.state.newAddedMembers
        .filter((ms: ProgramMember) =>
          (this.state.filterValue && this.state.filterValue !== '' ?
          (ms.programMemberDetail.level && ms.programMemberDetail.level.requiredAttendance > 0 ?
            ms.programMemberDetail.level.requiredAttendance - ms.programMemberDetail.completedAttendances
            === Number(this.state.filterValue) : false) : true)); 
        break;
      default:
        break;
    }
    this.setState({
      filteredData: filteredExisting,
      filteredNewAddedMembers: filteredNew   
    });
  }
    
  private toggleAll = () => {
    /*
      'toggleAll' is a tricky concept with any filterable table
      do you just select ALL the records that are in your data?
      OR
      do you only select ALL the records that are in the current filtered data?
      
      The latter makes more sense because 'selection' is a visual thing for the user.
      This is especially true if you are going to implement a set of external functions
      that act on the selected information (you would not want to DELETE the wrong thing!).
      
      So, to that end, access to the internals of ReactTable are required to get what is
      currently visible in the table (either on the current page or any other page).
      
      The HOC provides a method call 'getWrappedInstance' to get a ref to the wrapped
      ReactTable and then get the internal state and the 'sortedData'. 
      That can then be iterrated to get all the currently visible records and set
      the selection state.
    */
    const selectAll = !this.state.selectAll;
    const selection = [];
    if (selectAll) {
      // we need to get at the internals of ReactTable
      const wrappedInstance = this.checkboxTable.getWrappedInstance();
      // the 'sortedData' property contains the currently accessible records based on the filter and sort
      const currentRecords = wrappedInstance.getResolvedState().sortedData;
      // we just push all the IDs onto the selection array
      currentRecords.forEach((item) => {
        selection.push(item._original.id);
      });
    }
    this.setState({selectAll, selection});
  }

  private isSelected = (key) => {
    /*
      Instead of passing our external selection state we provide an 'isSelected'
      callback and detect the selection state ourselves. This allows any implementation
      for selection (either an array, object keys, or even a Javascript Set object).
    */
    return this.state.selection.includes(key);
  }

  private renderAddMemberDialog = (): JSX.Element => {
    const { toggleSelection, toggleAll, isSelected } = this;

    const { selectAll } = this.state;

    const checkboxProps = {
      selectAll,
      isSelected,
      toggleSelection,
      toggleAll,
      selectType: 'checkbox',
    };

    return(
      <Dialog
        actions={[this.renderAddMemberCancelButton(), this.renderAddMemberAddButton()]}
        modal={true}
        open={this.state.openAddMemberDialog}
        autoScrollBodyContent={true}
      >
        <div className="row mb-4 mt-4">
          <div className="col-6">
            <TextField
              floatingLabelText="Filter By Name"
              value={this.state.filterByNameAddNew}
              onChange={this.handleFilterByNameChangeAddNew}
              fullWidth={true}
            />
          </div>
          <div className="col-6">
            <TextField
              floatingLabelText="Filter By Phone"
              value={this.state.filterByPhoneAddNew}
              onChange={this.handleFilterByPhoneChangeAddNew}
              fullWidth={true}
            />
          </div>
        </div>
        <div className="row mb-4">
          <div
            className="col-12"
            style={overlayStyle(true)}
          >
            {renderOverlayLoading(true, '25%')}
            <CheckboxTable
              ref={(r) => this.checkboxTable = r}
              data={
                this.props.membersState.members
                .filter((m: Member) => 
                  (this.state.filterByNameAddNew ?
                    (m.firstName + ' ' + m.lastName).toLowerCase()
                    .includes(this.state.filterByNameAddNew.toLowerCase()) :
                    true) &&
                  (this.state.filterByPhoneAddNew ?
                    m.mobile1.includes(this.state.filterByPhoneAddNew) :
                    true) &&
                  (!this.state.editedExistingProgramMembers
                    .map((pm: ProgramMember) => pm.programMemberDetail.member.id)
                    .includes(m.id) || 
                  this.state.membersToBeRemoved
                    .map((pm: ProgramMember) => pm.programMemberDetail.member.id)
                    .includes(m.id)) &&
                  !this.state.newAddedMembers
                    .map((pm: ProgramMember) => pm.programMemberDetail.member.id)
                    .includes(m.id) &&
                  !m.cancelled
                )
                .map((m: Member) => this.createAddMemberTableData(m))
              }
              columns={[
                {
                  accessor: 'memberId',
                  Header: 'Member ID',
                  style: {textAlign: 'center'}
                },
                {
                  accessor: 'name',
                  Header: 'Name',
                  style: {textAlign: 'center'}
                },
                {
                  accessor: 'phone',
                  Header: 'Phone',
                  style: {textAlign: 'center'}
                }
              ]}
              defaultPageSize={20}
              className="-striped -highlight"
              keyField={'id'}
              showPageSizeOptions={false}
              {...checkboxProps}
            />
          </div>
        </div>
      </Dialog>
    );
  }

  private renderTestHistoryDialog = (): JSX.Element => {
    const selectedMember = this.state.filteredData[this.state.selectedTableRow];
    const selectedFilteredNewMember = this.state.filteredNewAddedMembers[
      this.state.selectedTableRow - this.state.filteredData.length
    ];

    return(
      <Dialog
        actions={[
          this.renderTestHistoryCancelButton(),
          this.renderTestHistorySubmitButton(
            selectedMember === undefined,
            selectedMember ? selectedMember : selectedFilteredNewMember
          )
        ]}
        modal={true}
        open={this.state.openTestHistoryDialog}
        autoScrollBodyContent={true}
      >
        <div className="row ma2 no-gutters">
          <div className="col-4">
            <div className="input-group mb-2 mr-sm-2 mb-sm-0">
              <SelectField
                fullWidth={true}
                floatingLabelText="Level"
                value={
                  this.state.selectedTestHistoryLevel ?
                    this.state.selectedTestHistoryLevel :
                    undefined
                }
                onChange={(e: React.SyntheticEvent<{}>, index: number, menuItemValue: string) =>
                  this.onTestHistoryLevelSelect(
                    menuItemValue,
                    selectedMember ? selectedMember : selectedFilteredNewMember ? selectedFilteredNewMember : undefined,
                    selectedFilteredNewMember !== undefined 
                  )}
              >
                {
                  this.state.editedLevels.map((l: Level, index: number) => {
                    return <MenuItem
                      key={index}
                      value={l.level}
                      primaryText={l.level}
                    />;
                  })
                }
              </SelectField>
            </div>
          </div>
          <div className="col-4">
            <div className="input-group mb-2 mr-sm-2 mb-sm-0">
              <SelectField
                fullWidth={true}
                floatingLabelText="Result"
                value={
                  this.state.selectedTestHistoryTestResult ?
                    this.state.selectedTestHistoryTestResult :
                    undefined
                }
                onChange={(e: React.SyntheticEvent<{}>, index: number, menuItemValue: TestResultCode) =>
                  this.onTestResultSelect(menuItemValue)}
              >
                {
                  testResults.map((testResult, index) => {
                    return <MenuItem
                      key={index}
                      value={testResult.code}
                      primaryText={testResult.name}
                    />;
                  })
                }
              </SelectField>
            </div>
          </div>
          <div className="col-4">
            <div className="input-group mb-2 mr-sm-2 mb-sm-0">
              <DatePicker
                fullWidth={true}
                className="w-100"
                floatingLabelText="Test Date"
                value={
                  this.state.selectedTestHistoryDate ?
                    this.state.selectedTestHistoryDate :
                    undefined
                }
                onChange={(event: React.FormEvent<HTMLInputElement>, date: Date) => this.onTestDateChange(date)}
              />
            </div>
          </div>
        </div>
      </Dialog>
    );
  }

  private onTestDateChange = (date: Date): void => {
    this.setState({selectedTestHistoryDate: date});
  }

  private onTestResultSelect = (result: TestResultCode): void => {
    this.setState({selectedTestHistoryTestResult: result});
  }

  private onTestHistoryLevelSelect = (l: string, pm: ProgramMember, isNew: boolean): void => {
    this.setState({selectedTestHistoryLevel: l});
  }

  private createAddMemberTableData = (m: Member): AddMemberTableData => {
    return {
      id: m.id,
      memberId: m.memberId,
      name: m.firstName + ' ' + m.lastName,
      phone: m.mobile1
    };
  }

  private renderAddMemberCancelButton = (): JSX.Element => {
    return(
      <RaisedButton
        label="CANCEL"
        onClick={this.onCancel}
        className="mr2 mt2"
      />
    );
  }

  private renderTestHistoryCancelButton = (): JSX.Element => {
    return(
      <RaisedButton
        label="CANCEL"
        onClick={this.onTestHistoryCancel}
        className="mr2 mt2"
      />
    );
  }

  private onAddMember = (): void => {
    this.setState(
      {
        newAddedMembers: [
          ...this.state.newAddedMembers,
          ...this.props.membersState.members
            .filter((m: Member) => this.state.selection.includes(m.id))
            .map((m: Member) => {
              if (
                this.state.editedExistingProgramMembers
                .map((p: ProgramMember) => p.programMemberDetail.member.id)
                .includes(m.id) &&
                this.state.membersToBeRemoved
                .map((p: ProgramMember) => p.programMemberDetail.member.id)
                .includes(m.id)
              ) {
                return undefined;
              } else {
                return {
                  programMemberDetail: {
                    id: 0,
                    member: m,
                    schedules: [],
                    level: this.props.programsState.programs
                      .find((p: Program) => p.id === this.props.programsState.selectedEvent.id)
                      .levels[0],
                    testHistories: [],
                    completedAttendances: 0,
                    entityStatus: CREATED
                  },
                  newTestHistories: [],
                  deletedTestHistories: []
                };
              }
            })
            .filter(Boolean)
        ],
        openAddMemberDialog: false,
        membersToBeRemoved: this.state.membersToBeRemoved
          .filter((pm: ProgramMember) => !this.state.selection.includes(pm.programMemberDetail.member.id))
      },
      () => {
        this.setState(
          {
            selection: []
          }
        );
        this.applyFilters();
      }
    );
  }

  private renderAddMemberAddButton = (): JSX.Element => {
    return(
      <RaisedButton
        label="ADD"
        primary={true} 
        disabled={this.state.selection.length === 0}
        onClick={this.onAddMember}
        className="mr2 mt2"
      />
    );
  }

  private onCancel = (): void => this.setState({openAddMemberDialog: false});

  private onTestHistoryCancel = (): void => this.setState({openTestHistoryDialog: false});

  private renderTestHistorySubmitButton = (isNew: boolean, pm: ProgramMember): JSX.Element => {
    return(
      <RaisedButton
        label="SUBMIT"
        primary={true}
        onClick={() => this.onSubmitTestHistory(isNew, pm)}
        disabled={
          !this.state.selectedTestHistoryDate ||
          !this.state.selectedTestHistoryLevel ||
          !this.state.selectedTestHistoryTestResult
        }
        className="mr2 mt2"
      />
    );
  }

  private onSubmitTestHistory = (isNew: boolean, pm: ProgramMember): void => {
    const foundPM = isNew ? 
      this.state.filteredNewAddedMembers.find((p: ProgramMember) => 
        p.programMemberDetail.member.id === pm.programMemberDetail.member.id) :
      this.state.filteredData.find((p: ProgramMember) => 
        p.programMemberDetail.member.id === pm.programMemberDetail.member.id);
    const i = isNew ?
      this.state.filteredNewAddedMembers.indexOf(foundPM) :
      this.state.filteredData.indexOf(foundPM);

    const existingPM = this.state.editedExistingProgramMembers.find((m: ProgramMember) => 
      m.programMemberDetail.member.id === pm.programMemberDetail.member.id);
    const existingIndex = this.state.editedExistingProgramMembers.indexOf(existingPM);

    const originalNewAdded = this.state.newAddedMembers.find((m: ProgramMember) =>
      m.programMemberDetail.member.id === pm.programMemberDetail.member.id);
    const originalIndex = this.state.newAddedMembers.indexOf(originalNewAdded);

    if (isNew) {
      this.setState(
        {
          newAddedMembers: [
            ...this.state.newAddedMembers.slice(0, originalIndex),
            {
              ...originalNewAdded,
              newTestHistories:
                [
                  ...originalNewAdded.newTestHistories,                  
                  {
                    memberId: originalNewAdded.programMemberDetail.member.id,
                    programId: this.props.programsState.selectedEvent.id,
                    levelId: this.state.editedLevels
                      .find((l: Level) => l.level === this.state.selectedTestHistoryLevel).id,
                    levelName: this.state.selectedTestHistoryLevel,
                    testDate: this.state.selectedTestHistoryDate.getTime(),
                    testResult: this.state.selectedTestHistoryTestResult,
                    entityStatus: CREATED,
                    programName: this.props.programsState.selectedEvent.title
                  }
                ]
            },
            ...this.state.newAddedMembers.slice(originalIndex + 1)
          ],
          filteredNewAddedMembers: [
            ...this.state.filteredNewAddedMembers.slice(0, i),
            {
              ...foundPM,
              newTestHistories:
                [
                  ...foundPM.newTestHistories,
                  {
                    memberId: foundPM.programMemberDetail.member.id,
                    programId: this.props.programsState.selectedEvent.id,
                    levelId: this.state.editedLevels
                      .find((l: Level) => l.level === this.state.selectedTestHistoryLevel).id,
                    levelName: this.state.selectedTestHistoryLevel,
                    testDate: this.state.selectedTestHistoryDate.getTime(),
                    testResult: this.state.selectedTestHistoryTestResult,
                    entityStatus: CREATED,
                    programName: this.props.programsState.selectedEvent.title
                  }
                ]
            },
            ...this.state.filteredNewAddedMembers.slice(i + 1)
          ]
        },
        () => {
          this.setState({openTestHistoryDialog: false});
        }
      );
    } else {
      this.setState(
        {
          editedExistingProgramMembers: [
            ...this.state.editedExistingProgramMembers.slice(0, existingIndex),
            {
              ...existingPM,
              newTestHistories:
                [
                  ...existingPM.newTestHistories,                  
                  {
                    memberId: existingPM.programMemberDetail.member.id,
                    programId: this.props.programsState.selectedEvent.id,
                    levelId: this.state.editedLevels
                      .find((l: Level) => l.level === this.state.selectedTestHistoryLevel).id,
                    levelName: this.state.selectedTestHistoryLevel,
                    testDate: this.state.selectedTestHistoryDate.getTime(),
                    testResult: this.state.selectedTestHistoryTestResult,
                    entityStatus: CREATED,
                    programName: this.props.programsState.programs
                      .find((p: Program) => p.id === this.props.programsState.selectedEvent.id)
                      .name
                  }
                ]
            },
            ...this.state.editedExistingProgramMembers.slice(existingIndex + 1)
          ],
          filteredData: [
            ...this.state.filteredData.slice(0, i),
            {
              ...foundPM,
              newTestHistories:
                [
                  ...foundPM.newTestHistories,
                  {
                    memberId: foundPM.programMemberDetail.member.id,
                    programId: this.props.programsState.selectedEvent.id,
                    levelId: this.state.editedLevels
                      .find((l: Level) => l.level === this.state.selectedTestHistoryLevel).id,
                    levelName: this.state.selectedTestHistoryLevel,
                    testDate: this.state.selectedTestHistoryDate.getTime(),
                    testResult: this.state.selectedTestHistoryTestResult,
                    entityStatus: CREATED,
                    programName: this.props.programsState.selectedEvent.title
                  }
                ]
            },
            ...this.state.filteredData.slice(i + 1)
          ]
        },
        () => {
          this.setState({openTestHistoryDialog: false});
        }
      );
    }
  }
}

export default ProgramDetailComponent;



// WEBPACK FOOTER //
// ./src/programs/ProgramDetailComponent.tsx